import { AnyAction } from 'redux';
import { App, Produktlinie, VwgVorbelegungdaten } from '@ps20-frontend/frontend-models';
import {
  AngebotsId,
  CoordinatesChangedPayload,
  TARIF_CHECKED,
  ZUSATZOPTION_CHECKED,
  ZUSATZOPTION_UNCHECKED
} from '@ps20-frontend/shop-integration-redux';
import { Injectable } from '@angular/core';
import { ExtremwetterschutzSelbstbeteiligung, Selbstbeteiligung } from '@ps20/bff-models';
import { AntragsummerGenerated } from './action-types/antragsummerGenerated';
import { Merkmal } from './models/merkmal.model';
import { ResidentialUnits } from './models';
import { GefahrChecked } from './action-types/gefahrChecked';
import { ExtremeWetterschutzSelbstbeteiligungValues } from './models/ExtremWetterschutzSelbstbeteiligungValues.model';

export interface VwgAction<VorschadenArt> extends AnyAction {
  payload?: VorschadenArt;
}

export interface VorschadenArt {
  feuer?: number;
  leitungswasser?: number;
  sturmhagel?: number;
  elementar?: number;
}

export interface VorschaedenVisibility {
  feuer?: boolean;
  leitungswasser?: boolean;
  sturmhagel?: boolean;
  elementar?: boolean;
}

export interface ExtremeWetterschutzSBReceived {
  angebotsId: string;
  values: ExtremeWetterschutzSelbstbeteiligungValues[];
}

@Injectable()
export class VwgActions {
  static readonly ABSPRUNG_INS_DASHBOARD = 'ABSPRUNG_INS_DASHBOARD';
  static readonly ANTRAGSNUMMER_GENERATED = 'ANTRAGSNUMMER_GENERATED';
  static readonly PLZ_CHANGED = 'PLZ_CHANGED';
  static readonly ORT_CHANGED = 'ORT_CHANGED';
  static readonly STRASSE_CHANGED = 'STRASSE_CHANGED';
  static readonly HAUSNUMMER_CHANGED = 'HAUSNUMMER_CHANGED';
  static readonly RESET_ADRESSDATEN_REQUESTED = 'RESET_ADRESSDATEN_REQUESTED';
  static readonly RESET_ADRESSDATEN = 'RESET_ADRESSDATEN';
  static readonly LAND_CHANGED = 'LAND_CHANGED';
  static readonly ORTSTEIL_CHANGED = 'ORTSTEIL_CHANGED';
  static readonly HAUSNRZUS_CHANGED = 'HAUSNRZUS_CHANGED';
  static readonly VORSCHAEDEN_CHANGED = 'VORSCHAEDEN_CHANGED';
  static readonly FEUER_CHANGED = 'FEUER_CHANGED';
  static readonly LEITUNGSWASSER_CHANGED = 'LEITUNGSWASSER_CHANGED';
  static readonly ELEMENTAR_CHANGED = 'ELEMENTAR_CHANGED';
  static readonly STURMHAGEL_CHANGED = 'STURMHAGEL_CHANGED';
  static readonly CONSTRUCTION_YEAR_CHANGED = 'CONSTRUCTION_YEAR_CHANGED';
  static readonly LIVING_AREA_CHANGED = 'LIVING_AREA_CHANGED';
  static readonly RESIDENTIAL_UNITS_CHANGED = 'RESIDENTIAL_UNITS_CHANGED';
  static readonly OUTBUILDING_CHANGED = 'OUTBUILDING_CHANGED';
  static readonly SIZE_CATEGORY_CHANGED = 'SIZE_CATEGORY_CHANGED';
  static readonly TOTAL_AREA_CHANGED = 'TOTAL_AREA_CHANGED';
  static readonly MERKMALE_CHANGED = 'MERKMALE_CHANGED';
  static readonly SELBSTBETEILIGUNG_CHANGED = 'SELBSTBETEILIGUNG_CHANGED';
  static readonly GEBURTSDATUM_CHANGED = 'GEBURTSDATUM_CHANGED';
  static readonly GEFAHREN_CHECKED = 'GEFAHREN_CHECKED';
  static readonly BRUTTOPREISE_CALCULATED = 'BRUTTOPREISE_CALCULATED';
  static readonly PRODUKTLINIEN_CHANGED = 'PRODUKTLINIEN_CHANGED';
  static readonly DISABLE_ELEMENTAR_ZUSATZOPTION = 'DISABLE_ELEMENTAR_ZUSATZOPTION';
  static readonly PRODUKT_VORBELEGT = 'PRODUKT_VORBELEGT';
  static readonly PREISE_REQUESTED = 'PREISE_REQUESTED';
  static readonly ZURUECK_ZUM_SHOP_MODAL_OPEN = 'ZURUECK_ZUM_SHOP_MODAL_OPEN';
  static readonly ZURUECK_ZUM_SHOP_MODAL_CLOSE = 'ZURUECK_ZUM_SHOP_MODAL_CLOSE';
  static readonly VORSCHLAGDATEN_CHANGED = 'VORSCHLAGDATEN_CHANGED';
  static readonly QUOTEDB_REQUESTED_VWG = 'QUOTEDB_REQUESTED_VWG';
  static readonly OELTANK_VORBELEGEN = 'OELTANK_VORBELEGEN';
  static readonly HANDLE_VWG_NAVIGATION_TO_ANGEBOT = 'HANDLE_VWG_NAVIGATION_TO_ANGEBOT';
  static readonly MAP_COORDINATES_VWG_CHANGED_EINGABE = 'MAP_COORDINATES_VWG_CHANGED_EINGABE';
  static readonly MAP_COORDINATES_VWG_CHANGED_ANGEBOT = 'MAP_COORDINATES_VWG_CHANGED_ANGEBOT';
  static readonly AUSLAENDISCHE_ADRESSE_CHANGED = 'AUSLAENDISCHE_ADRESSE_CHANGED';
  static readonly RISIKOORT_DATA_REQUESTED = 'RISIKOORT_DATA_REQUESTED';
  static readonly PLZ_REQUIRES_UW_CHECK_REQUESTED = 'PLZ_REQUIRES_UW_CHECK_REQUESTED';
  static readonly PLZ_REQUIRES_UW_RECEIVED = 'PLZ_REQUIRES_UW_RECEIVED';
  static readonly PLZ_REQUIRES_UW_CHECK_FAILED = 'PLZ_REQUIRES_UW_CHECK_FAILED';
  static readonly PLZ_REQUIRES_UW_SUBMISSION_BLOCKED = 'PLZ_REQUIRES_UW_SUBMISSION_BLOCKED';
  static readonly SHOW_VORSCHAEDEN_ERSATZGESCHAEFT = 'SHOW_VORSCHAEDEN_ERSATZGESCHAEFT';
  static readonly SHOW_ZUM_ANGEBOT_LOADING_SPINNER = 'SHOW_ZUM_ANGEBOT_LOADING_SPINNER';
  static readonly UPDATE_ELEMENTAR_VORSCHADEN_RISIKOORT = 'UPDATE_ELEMENTAR_VORSCHADEN_RISIKOORT';
  static readonly EXTREMWETTERSCHUTZ_SELBSTBETEILIGUNG_VALUES_RECEIVED =
    'EXTREMWETTERSCHUTZ_SELBSTBETEILIGUNG_VALUES_RECEIVED';
  static readonly EXTREMEWETTERSCHUTZ_SELBSTBETEILIGUNG_CHANGED =
    'EXTREMEWETTERSCHUTZ_SELBSTBETEILIGUNG_CHANGED';
  static readonly VERSICHERTE_NEBENGEBAEUDE_LEISTUNG_CHANGED = 'VERSICHERTE_NEBENGEBAEUDE_LEISTUNG_CHANGED';
  static readonly HAUS_TECHNIK_SCHUTZ_LEISTUNG_CHANGED = 'HAUS_TECHNIK_SCHUTZ_LEISTUNG_CHANGED';
  static readonly AUSSENPOOL_IN_HTS_CHANGED = 'AUSSENPOOL_IN_HTS_CHANGED';

  absprungInsDashboard(): AnyAction {
    return { type: VwgActions.ABSPRUNG_INS_DASHBOARD };
  }

  antragsnummerGenerated(
    antragsnummer: string,
    angebotsId: string
  ): VwgAction<AntragsummerGenerated> {
    return {
      type: VwgActions.ANTRAGSNUMMER_GENERATED,
      payload: { antragsnummer, angebotsId }
    };
  }

  plzChanged(plz: string): AnyAction {
    return { type: VwgActions.PLZ_CHANGED, payload: { plz } };
  }

  ortChanged(ort: string): VwgAction<{ ort: string }> {
    return { type: VwgActions.ORT_CHANGED, payload: { ort } };
  }

  disableElementarZusatzoption(): VwgAction<void> {
    return { type: VwgActions.DISABLE_ELEMENTAR_ZUSATZOPTION };
  }

  strasseChanged(strasse: string): VwgAction<{ strasse: string }> {
    return { type: VwgActions.STRASSE_CHANGED, payload: { strasse } };
  }

  hausnummerChanged(hausnummer: string): VwgAction<{ hausnummer: string }> {
    return { type: VwgActions.HAUSNUMMER_CHANGED, payload: { hausnummer } };
  }

  resetAdressdatenRequested(): VwgAction<void> {
    return { type: VwgActions.RESET_ADRESSDATEN_REQUESTED };
  }

  resetAdressdaten(): VwgAction<void> {
    return { type: VwgActions.RESET_ADRESSDATEN };
  }

  geburtsdatumChanged(geburtsdatum: string): VwgAction<{ geburtsdatum: string }> {
    return { type: VwgActions.GEBURTSDATUM_CHANGED, payload: { geburtsdatum } };
  }

  produktZusatzoptionenVorbelegt(
    vorbelegundaten: VwgVorbelegungdaten
  ): VwgAction<VwgVorbelegungdaten> {
    return {
      type: VwgActions.PRODUKT_VORBELEGT,
      payload: vorbelegundaten
    };
  }

  vorschaedenChanged(vorschaeden: boolean): VwgAction<{ vorschaeden: boolean }> {
    return { type: VwgActions.VORSCHAEDEN_CHANGED, payload: { vorschaeden } };
  }

  feuerChanged(feuer: number): VwgAction<VorschadenArt> {
    return { type: VwgActions.FEUER_CHANGED, payload: { feuer } };
  }

  leitungswasserChanged(leitungswasser: number): VwgAction<VorschadenArt> {
    return {
      type: VwgActions.LEITUNGSWASSER_CHANGED,
      payload: { leitungswasser }
    };
  }

  sturmhagelChanged(sturmhagel: number): VwgAction<VorschadenArt> {
    return { type: VwgActions.STURMHAGEL_CHANGED, payload: { sturmhagel } };
  }

  elementarChanged(elementar: number): VwgAction<VorschadenArt> {
    return { type: VwgActions.ELEMENTAR_CHANGED, payload: { elementar } };
  }

  constructionYearChanged(constructionYear: number): VwgAction<{ constructionYear: number }> {
    return { type: VwgActions.CONSTRUCTION_YEAR_CHANGED, payload: { constructionYear } };
  }

  livingAreaChanged(livingArea: number): VwgAction<{ livingArea: number }> {
    return { type: VwgActions.LIVING_AREA_CHANGED, payload: { livingArea } };
  }

  residentialUnitsChanged(residentialUnits: ResidentialUnits): VwgAction<{ residentialUnits: ResidentialUnits }> {
    return { type: VwgActions.RESIDENTIAL_UNITS_CHANGED, payload: { residentialUnits } };
  }

  outbuildingChanged(available: boolean): VwgAction<{ available: boolean }> {
    return { type: VwgActions.OUTBUILDING_CHANGED, payload: { available } };
  }

  sizeCategoryChanged(sizeCategory: string): VwgAction<{ sizeCategory: string }> {
    return {
      type: VwgActions.SIZE_CATEGORY_CHANGED,
      payload: { sizeCategory }
    };
  }

  totalAreaChanged(totalArea: number): VwgAction<{ totalArea: number }> {
    return {
      type: VwgActions.TOTAL_AREA_CHANGED,
      payload: { totalArea }
    };
  }

  merkmaleChanged(changedMerkmal: Merkmal): VwgAction<{ [merkmalName: string]: boolean }> {
    const payload: Record<string, boolean> = {};
    payload[changedMerkmal.name] = changedMerkmal.value;

    return { type: VwgActions.MERKMALE_CHANGED, payload };
  }

  selbstbeteiligungChanged(
    selbstbeteiligung: Selbstbeteiligung
  ): VwgAction<{ selbstbeteiligung: Selbstbeteiligung }> {
    return {
      type: VwgActions.SELBSTBETEILIGUNG_CHANGED,
      payload: { selbstbeteiligung }
    };
  }

  tarifChecked(index: number): VwgAction<{ index: number }> {
    return { type: TARIF_CHECKED, payload: { index } };
  }

  zusatzoptionChecked(name: string): VwgAction<{ name: string }> {
    return { type: ZUSATZOPTION_CHECKED, payload: { name } };
  }

  zusatzoptionUnchecked(name: string): VwgAction<{ name: string }> {
    return { type: ZUSATZOPTION_UNCHECKED, payload: { name } };
  }

  bruttopreiseCalculated(
    produktlinienMitBruttopreisen: Array<Produktlinie>
  ): VwgAction<Array<Produktlinie>> {
    return {
      type: VwgActions.BRUTTOPREISE_CALCULATED,
      payload: produktlinienMitBruttopreisen
    };
  }

  gefahrenChecked(changedGefahr: Array<GefahrChecked>): VwgAction<Array<GefahrChecked>> {
    return { type: VwgActions.GEFAHREN_CHECKED, payload: changedGefahr };
  }

  produktlinienChanged(produktlinien: Produktlinie[]): VwgAction<Produktlinie[]> {
    return { type: VwgActions.PRODUKTLINIEN_CHANGED, payload: produktlinien };
  }

  zurueckZumShopModalOpen(): VwgAction<void> {
    return { type: VwgActions.ZURUECK_ZUM_SHOP_MODAL_OPEN };
  }

  zurueckZumShopModalClose(): VwgAction<void> {
    return { type: VwgActions.ZURUECK_ZUM_SHOP_MODAL_CLOSE };
  }

  vorschlagdatenChanged(angebotsId: string): VwgAction<string> {
    return { type: VwgActions.VORSCHLAGDATEN_CHANGED, payload: angebotsId };
  }

  oeltankVorbelegen(): AnyAction {
    return { type: VwgActions.OELTANK_VORBELEGEN };
  }

  quoteDBRequested(): AnyAction {
    return { type: VwgActions.QUOTEDB_REQUESTED_VWG };
  }

  handleVwgNavigationToAngebot(): VwgAction<void> {
    return { type: VwgActions.HANDLE_VWG_NAVIGATION_TO_ANGEBOT };
  }

  preiseRequested(): VwgAction<void> {
    return { type: VwgActions.PREISE_REQUESTED };
  }

  mapCoordinatesChangedEingabe(
    coordinatesChangedPayload: CoordinatesChangedPayload
  ): VwgAction<CoordinatesChangedPayload> {
    return {
      type: VwgActions.MAP_COORDINATES_VWG_CHANGED_EINGABE,
      payload: coordinatesChangedPayload
    };
  }

  mapCoordinatesChangedAngebot(
    coordinatesChangedPayload: CoordinatesChangedPayload
  ): VwgAction<CoordinatesChangedPayload> {
    return {
      type: VwgActions.MAP_COORDINATES_VWG_CHANGED_ANGEBOT,
      payload: coordinatesChangedPayload
    };
  }

  auslaendischeAdresseCheckboxChanged(isCheckboxChecked: boolean): VwgAction<boolean> {
    return {
      type: VwgActions.AUSLAENDISCHE_ADRESSE_CHANGED,
      payload: isCheckboxChecked
    };
  }

  risikoortDataRequested(angebotsId: string): VwgAction<AngebotsId> {
    return {
      type: VwgActions.RISIKOORT_DATA_REQUESTED,
      payload: {
        appId: App.VWG_V2,
        angebotsId
      }
    };
  }

  plzRequiresUwCheckRequested(angebotsId: string): VwgAction<AngebotsId> {
    return {
      type: VwgActions.PLZ_REQUIRES_UW_CHECK_REQUESTED,
      payload: {
        appId: App.VWG_V2,
        angebotsId
      }
    };
  }

  plzRequiresUwReceived(requiresUw: boolean): VwgAction<boolean> {
    return { type: VwgActions.PLZ_REQUIRES_UW_RECEIVED, payload: requiresUw };
  }

  plzRequiresUwCheckFailed(): VwgAction<void> {
    return { type: VwgActions.PLZ_REQUIRES_UW_CHECK_FAILED };
  }

  plzRequiresUwSubmissionBlocked(): VwgAction<void> {
    return { type: VwgActions.PLZ_REQUIRES_UW_SUBMISSION_BLOCKED };
  }

  showVorschaedenErsatzgeschaeft(
    visibility: VorschaedenVisibility
  ): VwgAction<VorschaedenVisibility> {
    return {
      type: VwgActions.SHOW_VORSCHAEDEN_ERSATZGESCHAEFT,
      payload: visibility
    };
  }

  showZumAngebotLoadingSpinner(show: boolean): VwgAction<boolean> {
    return {
      type: VwgActions.SHOW_ZUM_ANGEBOT_LOADING_SPINNER,
      payload: show
    };
  }

  updateElementarVorschadenFromRisikoortService(count: number): VwgAction<number> {
    return {
      type: VwgActions.UPDATE_ELEMENTAR_VORSCHADEN_RISIKOORT,
      payload: count
    };
  }

  extremWetterschutzSelbstbeteiligungReceived(
    extremewetterschutzSelbstbeteiligungValuesList: ExtremeWetterschutzSelbstbeteiligungValues[],
    angebotsId: string
  ): VwgAction<ExtremeWetterschutzSBReceived> {
    return {
      type: VwgActions.EXTREMWETTERSCHUTZ_SELBSTBETEILIGUNG_VALUES_RECEIVED,
      payload: { angebotsId: angebotsId, values: extremewetterschutzSelbstbeteiligungValuesList }
    };
  }

  extremWetterschutzSelbstbeteiligungChanged(
    extremwetterschutzSelbstbeteiligung: ExtremwetterschutzSelbstbeteiligung,
    angebotsId: string
  ): VwgAction<{
    extremWetterschutzSelbstbeteiligung: ExtremwetterschutzSelbstbeteiligung;
    angebotsId: string;
  }> {
    return {
      type: VwgActions.EXTREMEWETTERSCHUTZ_SELBSTBETEILIGUNG_CHANGED,
      payload: {
        extremWetterschutzSelbstbeteiligung: extremwetterschutzSelbstbeteiligung,
        angebotsId: angebotsId
      }
    };
  }

  versicherteNebengebaeudeLeistungChanged( abgewaehlt: boolean): VwgAction<{ abgewaehlt: boolean }> {
    return {
      type: VwgActions.VERSICHERTE_NEBENGEBAEUDE_LEISTUNG_CHANGED,
      payload: { abgewaehlt }
    };
  }

  hausTechnikSchutzLeistungChanged(produktlinieName: string, abgewaehlt: boolean): VwgAction<{ produktlinieName: string; abgewaehlt: boolean }> {
    return {
      type: VwgActions.HAUS_TECHNIK_SCHUTZ_LEISTUNG_CHANGED,
      payload: { produktlinieName, abgewaehlt }
    };
  }

  aussenpoolInHtsChanged(selected: boolean): VwgAction<boolean> {
    return {
      type: VwgActions.AUSSENPOOL_IN_HTS_CHANGED,
      payload: selected
    };
  }

}
